/**
 * Artigos do blog (ordem de exibição) e utilitários
 *
 * Para publicar um artigo novo: crie o objeto num dos arquivos de
 * articles/, com slug único. O build já inclui a página na pré-renderização,
 * no sitemap e no llms.txt.
 */

import type { BlogArticle, BlogArticleWithMeta, BlogBlock } from './types';
import { VENDAS_ARTICLES } from './articles/vendas';
import { DELIVERY_ARTICLES } from './articles/delivery';
import { GESTAO_ARTICLES } from './articles/gestao';
import { SEGMENTOS_ARTICLES } from './articles/segmentos';

export const BLOG_TITLE = 'Blog do Vende Aí: delivery, vendas e gestão para restaurantes';
export const BLOG_DESCRIPTION =
  'Guias práticos sobre sistema de delivery, robô de WhatsApp, cardápio digital, comissão de marketplace, PIX, fidelização e gestão para restaurantes, pizzarias, padarias e barbearias.';

/** Remove a marcação de negrito e links, deixando só o texto */
export function plainText(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

function blockText(block: BlogBlock): string {
  switch (block.type) {
    case 'ul':
    case 'ol':
      return block.items.join(' ');
    case 'table':
      return [...block.head, ...block.rows.flat()].join(' ');
    case 'tip':
      return `${block.title ?? ''} ${block.text}`;
    default:
      return block.text;
  }
}

function readingMinutes(article: BlogArticle): number {
  const words = article.blocks.map(blockText).join(' ').split(/\s+/).filter(Boolean).length;
  return Math.max(2, Math.ceil(words / 200));
}

const withMeta = (a: BlogArticle): BlogArticleWithMeta => ({
  ...a,
  readingMinutes: readingMinutes(a),
});

export const BLOG_ARTICLES: BlogArticleWithMeta[] = [
  ...VENDAS_ARTICLES,
  ...DELIVERY_ARTICLES,
  ...GESTAO_ARTICLES,
  ...SEGMENTOS_ARTICLES,
].map(withMeta);

export function findArticle(slug: string | undefined): BlogArticleWithMeta | undefined {
  return BLOG_ARTICLES.find(a => a.slug === slug);
}

/** Artigos relacionados: mesma categoria ou mesmo segmento primeiro */
export function relatedArticles(article: BlogArticleWithMeta, limit = 3): BlogArticleWithMeta[] {
  const score = (a: BlogArticleWithMeta) =>
    (a.category === article.category ? 2 : 0) +
    (article.relatedSegment && a.relatedSegment === article.relatedSegment ? 3 : 0);
  return BLOG_ARTICLES.filter(a => a.slug !== article.slug)
    .sort((a, b) => score(b) - score(a))
    .slice(0, limit);
}

export const BLOG_CATEGORIES = Array.from(new Set(BLOG_ARTICLES.map(a => a.category)));

/** Rotas públicas fora do blog que os artigos podem citar */
const KNOWN_PATHS = ['/', '/cadastro', '/entrar'];

/**
 * Confere a integridade dos artigos (roda no build, em scripts/prerender.mjs):
 * slugs únicos e válidos, datas, tamanhos de título/descrição, segmento
 * relacionado existente e links internos apontando para páginas que existem.
 *
 * @param segments páginas /para/<slug> e o tipo de negócio usado no cadastro
 * @returns lista de problemas (vazia quando está tudo certo)
 */
export function validateBlog(segments: { slug: string; type: string }[]): string[] {
  const segmentSlugs = segments.map(s => s.slug);
  const segmentTypes = segments.map(s => s.type);
  const errors: string[] = [];
  const slugs = new Set<string>();
  const validPath = (href: string) => {
    const pathname = href.split(/[?#]/)[0] || '/';
    if (KNOWN_PATHS.includes(pathname) || pathname === '/blog') return true;
    const article = pathname.match(/^\/blog\/([^/]+)$/);
    if (article) return BLOG_ARTICLES.some(a => a.slug === article[1]);
    const segment = pathname.match(/^\/para\/([^/]+)$/);
    return Boolean(segment && segmentSlugs.includes(segment[1]));
  };
  const texts = (a: BlogArticle) => [...a.blocks.map(blockText), ...a.faq.map(f => f.answer)];

  for (const a of BLOG_ARTICLES) {
    const where = `blog/${a.slug}`;
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(a.slug)) errors.push(`${where}: slug inválido`);
    if (slugs.has(a.slug)) errors.push(`${where}: slug repetido`);
    slugs.add(a.slug);
    if (a.title.length > 90) errors.push(`${where}: título com mais de 90 caracteres`);
    if (a.description.length < 70 || a.description.length > 170) {
      errors.push(
        `${where}: descrição deve ter entre 70 e 170 caracteres (${a.description.length})`,
      );
    }
    for (const d of [a.publishedAt, a.updatedAt]) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(Date.parse(d))) {
        errors.push(`${where}: data inválida "${d}"`);
      }
    }
    if (a.updatedAt < a.publishedAt) errors.push(`${where}: updatedAt anterior a publishedAt`);
    if (a.relatedSegment && !segmentSlugs.includes(a.relatedSegment)) {
      errors.push(`${where}: relatedSegment "${a.relatedSegment}" não existe`);
    }
    for (const b of a.blocks) {
      if (b.type === 'cta' && b.segment && !segmentTypes.includes(b.segment)) {
        errors.push(
          `${where}: segmento do CTA "${b.segment}" não é um tipo de negócio do cadastro`,
        );
      }
    }
    if (!a.blocks.some(b => b.type === 'h2')) errors.push(`${where}: sem intertítulos (h2)`);
    for (const text of texts(a)) {
      for (const [, href] of text.matchAll(/\]\(([^)]+)\)/g)) {
        if (href.startsWith('/') && !validPath(href)) {
          errors.push(`${where}: link interno quebrado "${href}"`);
        } else if (!href.startsWith('/') && !/^https:\/\//.test(href)) {
          errors.push(`${where}: link "${href}" deve começar com / ou https://`);
        }
      }
    }
  }
  return errors;
}
